import React, { lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { Toaster } from 'react-hot-toast';

import { baseTheme } from 'theme/variables';
import AuthProvider from 'context/AuthProvider';
import LocationProvider from 'context/LocationContextProvider';

// layout
import FullPageLayout from 'components/Layouts/FullPageLayout';
import AppLayout from 'components/Layouts/AppLayout';

// components
import Loadable from 'components/Loadable/Loadable';
import ProtectedRoute from 'components/ProtectedRoute';
import ClientWrapper from 'views/Clients/ClientWrapper';
import SOWDetailView from 'views/Ops/SOWDetailView';

// auth-pages
const Login = Loadable(lazy(() => import('views/Auth/Login')));

// page-views
const Dashboard = Loadable(lazy(() => import('views/Dashboard')));
const Freelancers = Loadable(lazy(() => import('views/Freelancers')));
const FreelancerDetails = Loadable(
  lazy(() => import('views/Freelancers/FreelancerDetails'))
);
const Clients = Loadable(lazy(() => import('views/Clients')));
const ClientDetails = Loadable(
  lazy(() => import('views/Clients/ClientDetails'))
);
const EditClientDetails = Loadable(
  lazy(() => import('views/Clients/EditClientDetails'))
);
const GigForm = Loadable(lazy(() => import('views/Clients/GigForm')));
const GigManagment = Loadable(lazy(() => import('views/GigManagment')));
const GigDetailsContainer = Loadable(
  lazy(() => import('views/GigManagment/GigDetailsContainer'))
);
const Messages = Loadable(lazy(() => import('views/Messages')));
const ProjectManagment = Loadable(lazy(() => import('views/ProjectManagment')));
const ProjectDetailView = Loadable(
  lazy(() => import('views/ProjectManagment/ProjectDetailView'))
);
const OpsDashboard = Loadable(lazy(() => import('views/Ops')));
const SkillsManagement = Loadable(lazy(() => import('views/SkillsManagement')));
const UserSettings = Loadable(lazy(() => import('views/UserSettings')));
const PaymentsInvoice = Loadable(lazy(() => import('views/PaymentsInvoice')));
const UiKit = Loadable(lazy(() => import('views/UiKit')));
const NotFound = Loadable(lazy(() => import('views/NotFound')));

const ROLES = {
  User: 2001,
  Editor: 1984,
  Admin: 5150,
};

const App = () => {
  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <ThemeProvider theme={baseTheme}>
        <LocationProvider>
          <AuthProvider>
            <Toaster
              toastOptions={{
                style: {
                  fontSize: '12px',
                  fontFamily: "'DM Sans', sans-serif",
                },
              }}
            />
            <Routes>
              <Route path='/'>
                {/* public routes */}
                <Route path='auth' element={<FullPageLayout />}>
                  <Route path='login' element={<Login />} />
                </Route>

                {/* protected routes */}
                <Route path='/' element={<AppLayout />}>
                  <Route
                    element={<ProtectedRoute allowedRoles={[ROLES.Admin]} />}
                  >
                    <Route index element={<Navigate to='dashboard' />} />
                    <Route path='dashboard/*' element={<Dashboard />} />
                  </Route>

                  <Route
                    element={<ProtectedRoute allowedRoles={[ROLES.Admin]} />}
                  >
                    <Route path='freelancers' element={<Freelancers />} />
                    <Route
                      path='freelancer/:freelancerId'
                      element={<FreelancerDetails />}
                    />
                    <Route path='clients' element={<Clients />} />
                    <Route path='client/:clientId' element={<ClientWrapper />}>
                      <Route index element={<ClientDetails />} />
                      <Route path='edit' element={<EditClientDetails />} />
                      <Route path='create-gig' element={<GigForm />} />
                      <Route path='gig/:gigId/edit' element={<GigForm />} />
                      <Route
                        path='gig/:gigId'
                        element={<GigDetailsContainer />}
                      />
                    </Route>
                  </Route>

                  <Route
                    element={
                      <ProtectedRoute
                        allowedRoles={[ROLES.Editor, ROLES.Admin]}
                      />
                    }
                  >
                    <Route path='gigs' element={<GigManagment />} />
                    <Route path='gig'>
                      <Route path=':gigId/edit' element={<GigForm />} />
                      <Route path=':gigId' element={<GigDetailsContainer />} />
                    </Route>
                    <Route path='messages' element={<Messages />} />
                    <Route path='projects' element={<ProjectManagment />} />
                    <Route
                      path='project/:projectId'
                      element={<ProjectDetailView />}
                    />
                    <Route path='ops'>
                      <Route index element={<OpsDashboard />} />
                      <Route path='sows/:sowId' element={<SOWDetailView />} />
                    </Route>
                    <Route path='skills' element={<SkillsManagement />} />
                    <Route path='settings' element={<UserSettings />} />
                    <Route path='transactions' element={<PaymentsInvoice />} />
                    <Route path='invoices' element={<PaymentsInvoice />} />
                    <Route path='ui-kit' element={<UiKit />} />
                  </Route>
                </Route>

                <Route
                  path='unauthorized'
                  element={
                    <NotFound message='Looks like you do no have access to this page.' />
                  }
                />
                <Route path='*' element={<NotFound />} />
              </Route>
            </Routes>
          </AuthProvider>
        </LocationProvider>
      </ThemeProvider>
    </LocalizationProvider>
  );
};

export default App;
